import { useState, useCallback } from "react";
import {
  useRemovePaymentPrefFromAccountMutation,
  useRemovePaymentPrefFromPositionMutation,
} from "../../../../../graphql/supergraph.index";
import { WorkflowType } from "../../../common/enums";
import { type DeletePaymentPrefParams, type UseDeletePaymentPrefReturn } from "../hooks/types";

/**
 * Custom hook to handle the deletion of payment preferences.
 *
 * This hook manages the state and logic for deleting payment preferences from either an account or a position.
 * It provides a function to initiate the deletion process and manages loading and error states.
 *
 * @param {DeletePaymentPrefParams} params - Parameters for deleting the payment preference.
 * @param {WorkflowType} params.workflowType - The type of workflow, either "Account" or "Position".
 * @param {string} [params.accountId] - The ID of the account. Required if workflowType is "Account".
 * @param {string} [params.positionId] - The ID of the position. Required if workflowType is "Position".
 * @param {function} [params.onSuccess] - A callback called after deletion occurs to enable consumers to refetch queries etc.
 *
 * @returns {Object} - The hook returns an object containing the following properties and methods:
 * @returns {string | undefined} deletePaymentPrefId - The ID of the payment preference to be deleted.
 * @returns {function} setDeletePaymentPrefId - Function to set the ID of the payment preference to be deleted.
 * @returns {boolean} loading - Indicates whether the deletion process is in progress.
 * @returns {Error | null} error - Contains any error that occurred during the deletion process.
 * @returns {function} deletePaymentPrefCallback - Function to initiate the deletion process.
 *
 * @example
 * const {
 *   deletePaymentPrefId,
 *   setDeletePaymentPrefId,
 *   loading,
 *   error,
 *   deletePaymentPrefCallback,
 * } = useDeletePaymentPref({ workflowType: WorkflowType.Account, accountId: "exampleAccountId" });
 *
 * // Set the payment preference ID to be deleted
 * setDeletePaymentPrefId("examplePaymentPrefId");
 *
 * // Initiate the deletion process
 * deletePaymentPrefCallback();
 */
export const useDeletePaymentPref = (
  params: DeletePaymentPrefParams,
): UseDeletePaymentPrefReturn => {
  const [deletePaymentPrefId, setDeletePaymentPrefId] = useState<string | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [
    doRemovePaymentPrefFromAccountMutation,
    { loading: doRemovePaymentPrefFromAccountMutationLoading },
  ] = useRemovePaymentPrefFromAccountMutation();

  const [
    doRemovePaymentPrefFromPositionMutation,
    { loading: doRemovePaymentPrefFromPositionMutationLoading },
  ] = useRemovePaymentPrefFromPositionMutation();

  const deletePaymentPrefCallback = useCallback(async () => {
    if (!deletePaymentPrefId) {
      return Promise.reject(new Error("No payment preference ID provided"));
    }

    setLoading(true);
    setError(null);

    try {
      let result;
      if (params.workflowType === WorkflowType.Account) {
        const { accountId } = params;
        result = await doRemovePaymentPrefFromAccountMutation({
          variables: {
            input: {
              accountId,
              paymentPrefId: deletePaymentPrefId,
            },
          },
        });
      } else if (params.workflowType === WorkflowType.Position) {
        const { positionId } = params;
        result = await doRemovePaymentPrefFromPositionMutation({
          variables: {
            input: {
              positionId,
            },
          },
        });
      }

      if (result?.data) {
        setLoading(false);
        setDeletePaymentPrefId(undefined); // Reset the ID after deletion

        // Enable consumers to react to changes
        if (params.workflowType === WorkflowType.Account) {
          params.onSuccess({ workflowType: params.workflowType });
        } else if (params.workflowType === WorkflowType.Position) {
          params.onSuccess({ workflowType: params.workflowType });
        }

        return Promise.resolve();
      }
      throw new Error("Failed to delete payment preference");
    } catch (err) {
      setLoading(false);
      setError(err as Error);
      return Promise.reject(err);
    }
  }, [
    deletePaymentPrefId,
    params,
    doRemovePaymentPrefFromAccountMutation,
    doRemovePaymentPrefFromPositionMutation,
    setDeletePaymentPrefId,
  ]);

  return {
    handleDeletePaymentInstructionClick: setDeletePaymentPrefId,
    deletePaymentPrefId,
    showDeletePaymentPrefModal: !!deletePaymentPrefId,
    handleCancelDeletePaymentPref: () => setDeletePaymentPrefId(undefined),
    deletePaymentPrefCallback,
    loading:
      loading ||
      doRemovePaymentPrefFromAccountMutationLoading ||
      doRemovePaymentPrefFromPositionMutationLoading,
    error,
  };
};
